import axios from "axios";
import { API_BASE_URL } from "../../config";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const affiliateAPI = {
  // Get all affiliate partners
  getAll: async () => {
    try {
      console.log("Fetching all affiliate partners...");
      const response = await api.get("/affiliate-partners");
      console.log("Raw API Response:", response.data);

      if (!response.data) {
        throw new Error("No data received from server");
      }

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to fetch partners");
      }

      return response.data;
    } catch (error) {
      console.error("Error in getAll:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
      }
      throw error;
    }
  },

  // Get affiliate partner by ID
  getById: async (id) => {
    try {
      const response = await api.get(`/affiliate-partners/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getByUser: async (userId) => {
    try {
      const response = await api.get(`/affiliate-partners/not-applied/${userId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
   getCutomerLoansByUser: async (userId) => {
    try {
      const response = await api.get(`/affiliate-partners/applied/${userId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Create new affiliate partner
  create: async (affiliateData) => {
    try {
      const response = await api.post("/affiliate-partners", affiliateData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update affiliate partner
  update: async (id, affiliateData) => {
    try {
      const response = await api.put(
        `/affiliate-partners/${id}`,
        affiliateData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete affiliate partner
  delete: async (id) => {
    try {
      const response = await api.delete(`/affiliate-partners/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get affiliate statistics
  getStats: async () => {
    try {
      console.log("Fetching affiliate stats...");
      const response = await api.get("/affiliate-partners", {
        params: { stats: true },
      });
      console.log("Raw Stats Response:", response.data);

      if (!response.data) {
        throw new Error("No data received from server");
      }

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to fetch stats");
      }

      return response.data;
    } catch (error) {
      console.error("Error in getStats:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
      }
      throw error;
    }
  },

  // Update affiliate status
  updateStatus: async (id, status) => {
    try {
      const response = await api.patch(`/affiliate-partners/${id}/status`, {
        status,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Upload document
  uploadDocument: async (file, type, affiliateId) => {
    try {
      console.log("Uploading document:", { file, type, affiliateId });

      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", type); // NOTE: sending 'type' not 'documentType'

      console.log("FormData contents:", {
        file: file.name,
        type,
        affiliateId,
      });

      const response = await api.post(
        `/affiliate-partners/upload/${affiliateId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to upload document");
      }

      return response.data;
    } catch (error) {
      console.error(
        "Upload error details:",
        error.response?.data || error.message
      );
      throw error.response?.data || error.message;
    }
  },

  // Search affiliate partners
  search: async (query) => {
    try {
      const response = await api.get("/affiliate-partners/search", {
        params: { query },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Export affiliate data
  exportData: async (format = "csv") => {
    try {
      const response = await api.get(`/affiliate-partners/export/${format}`, {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get affiliate partner profile by ID
  getAffiliateProfile: async (id) => {
    try {
      // Add validation before making the API call
      if (!id || id === "undefined") {
        throw new Error("Invalid partner ID");
      }

      console.log("Fetching affiliate profile for ID:", id);
      const response = await api.get(`/affiliate-partners/${id}/profile`);
      console.log("API Response:", response.data);

      if (!response.data) {
        throw new Error("No data received from server");
      }

      if (!response.data.success) {
        throw new Error(
          response.data.message || "Failed to fetch profile data"
        );
      }

      return response.data;
    } catch (error) {
      console.error("Error in getAffiliateProfile:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
      }
      throw error;
    }
  },

  // Get current user's affiliate partner profile
  getCurrentUserProfile: async () => {
    try {
      console.log("Fetching current user affiliate profile");
      const response = await api.get(`/affiliate-partners/profile/me`);
      console.log("API Response:", response.data);

      if (!response.data) {
        throw new Error("No data received from server");
      }

      if (!response.data.success) {
        throw new Error(
          response.data.message || "Failed to fetch profile data"
        );
      }

      return response.data;
    } catch (error) {
      console.error("Error in getCurrentUserProfile:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
      }
      throw error;
    }
  },

  // Update affiliate partner status
  updateAffiliateStatus: async (id, status) => {
    try {
      const response = await api.patch(`/affiliate-partners/${id}/status`, {
        status,
      });
      return response.data;
    } catch (error) {
      console.error("Error updating affiliate status:", error);
      throw error;
    }
  },

  // Download affiliate documents
  downloadAffiliateDocument: async (id, documentType) => {
    try {
      const response = await api.get(
        `/affiliate-partners/${id}/documents/${documentType}`,
        {
          responseType: "blob",
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error downloading document:", error);
      throw error;
    }
  },

  getAffiliateCustomers: async (partnerId) => {
    try {
      if (!partnerId || partnerId === "undefined") {
        throw new Error("Invalid partner ID");
      }
      const response = await api.get(
        `/affiliate-partners/${partnerId}/customers`
      );
      if (!response.data) {
        throw new Error("No data received from server");
      }
      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to fetch customers");
      }
      return response.data;
    } catch (error) {
      console.error("Error in getAffiliateCustomers:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
      }
      throw error;
    }
  },

  // Get affiliate leads
  getAffiliateLeads: async (partnerId) => {
    try {
      // Add validation before making the API call
      if (!partnerId || partnerId === "undefined") {
        throw new Error("Invalid partner ID");
      }
      const response = await api.get(`/affiliate-partners/${partnerId}/leads`);
      if (!response.data) {
        throw new Error("No data received from server");
      }
      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to fetch leads");
      }
      return response.data;
    } catch (error) {
      console.error("Error in getAffiliateLeads:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
      }
      throw error;
    }
  },

  // Verify affiliate document
  verifyDocument: async (id, documentType) => {
    try {
      const response = await api.patch(
        `/affiliate-partners/${id}/documents/verify`,
        { documentType }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};
